import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Target, Languages, Check } from "lucide-react";

import GradientOrb from "@/components/smartpublish/GradientOrb";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH (per Emergent Google Auth playbook).

export default function LandingPage() {
    const startAuth = () => {
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#0B0F19] text-[#E8ECF5]">
            <GradientOrb />

            <header className="relative z-10 mx-auto flex h-16 w-full max-w-[1100px] items-center justify-between px-6">
                <div
                    className="flex items-center gap-1 text-base font-medium tracking-wide"
                    data-testid="landing-wordmark"
                >
                    <span className="sp-glow-mint font-bold">S</span>
                    <span>martPublish</span>
                </div>
                <button
                    type="button"
                    onClick={startAuth}
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-4 py-2 text-xs font-medium text-white transition-all hover:shadow-[0_0_24px_rgba(0,245,212,0.4)]"
                    data-testid="landing-signin-top"
                >
                    Sign in
                </button>
            </header>

            <main className="relative z-10 mx-auto flex w-full max-w-[960px] flex-col items-start gap-10 px-6 py-20 sm:py-28">
                <motion.span
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F5D4]"
                    data-testid="hero-eyebrow"
                >
                    smartpublish · co-pilot for creators
                </motion.span>

                <motion.h1
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.05 }}
                    className="max-w-[18ch] text-5xl font-medium leading-[1.05] tracking-[-0.02em] text-[#E8ECF5] sm:text-6xl"
                    data-testid="hero-title"
                >
                    AI co-pilot for content creators.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="max-w-[58ch] text-base text-[#8A93A6] sm:text-lg"
                    data-testid="hero-subline"
                >
                    Generate platform-ready titles, captions, and audit your
                    posts with Brand-Director-grade AI. 15-day free trial, no
                    credit card.
                </motion.p>

                <motion.button
                    type="button"
                    onClick={startAuth}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.25 }}
                    className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-6 py-3 text-base font-medium text-white shadow-[0_0_24px_rgba(0,245,212,0.25)] transition-all hover:shadow-[0_0_36px_rgba(0,245,212,0.5)]"
                    data-testid="landing-google-signin-btn"
                >
                    <span className="relative z-10 inline-flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        Sign in with Google to start your 15-day trial
                    </span>
                    <span
                        aria-hidden
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00F5D4]/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    />
                </motion.button>

                <div
                    className="mt-12 grid w-full grid-cols-1 gap-4 sm:grid-cols-3"
                    data-testid="features-grid"
                >
                    <FeatureCard
                        title="Agent 1 · Copy"
                        body="Drop a video, image, or text. Get 3 platform-tuned titles + captions in ~20s."
                        Icon={Sparkles}
                    />
                    <FeatureCard
                        title="Agent 2 · Strategy"
                        body="Audience- and demographic-aware best-time-to-post predictions."
                        Icon={Target}
                        pro
                    />
                    <FeatureCard
                        title="Adsum · QC"
                        body="Pre-publish brand audit with score, pillar bars, and one-click rewrite."
                        Icon={ShieldCheck}
                        pro
                    />
                </div>

                <ul
                    className="mt-4 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs text-[#8A93A6]"
                    data-testid="trust-row"
                >
                    <li className="inline-flex items-center gap-1.5">
                        <Check className="h-3 w-3 text-[#00F5D4]" /> 15-day trial
                    </li>
                    <li className="inline-flex items-center gap-1.5">
                        <Check className="h-3 w-3 text-[#00F5D4]" /> 50 NXT welcome
                    </li>
                    <li className="inline-flex items-center gap-1.5">
                        <Languages className="h-3 w-3 text-[#00F5D4]" /> EN · ES on Pro
                    </li>
                </ul>
            </main>

            <footer className="relative z-10 mx-auto w-full max-w-[1100px] px-6 pb-10 pt-4">
                <p className="font-mono text-xs tracking-wide text-[#8A93A6]/80">
                    SmartPublish · orchestrating content
                </p>
            </footer>
        </div>
    );
}

function FeatureCard({ title, body, Icon, pro = false }) {
    return (
        <div className="relative flex flex-col gap-2 rounded-2xl border border-[#1F2A40] bg-[#111726] p-5 transition-colors hover:border-[#00F5D4]/30">
            {pro && (
                <span
                    className="absolute right-3 top-3 rounded-full border border-[#00F5D4]/40 bg-[#00F5D4]/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-[#00F5D4]"
                    data-testid="feature-pro-badge"
                >
                    pro
                </span>
            )}
            <Icon className="h-5 w-5 text-[#00F5D4]" />
            <h3 className="text-sm font-medium text-[#E8ECF5]">{title}</h3>
            <p className="text-xs leading-relaxed text-[#8A93A6]">{body}</p>
        </div>
    );
}
